import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type AuditLog = components["schemas"]["ClientModuleAuditLogResponse"];
type ModuleGrantState = components["schemas"]["ModuleGrantState"];

interface ModuleAuditLogsTableProps {
  clientId?: string;
  moduleKey?: string;
  toState?: ModuleGrantState;
  refreshKey?: number;
}

export function ModuleAuditLogsTable({
  clientId,
  moduleKey,
  toState,
  refreshKey,
}: ModuleAuditLogsTableProps) {
  const pagination = useCursorPagination({ initialLimit: 50 });
  const { reset } = pagination;

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reset();
  }, [clientId, moduleKey, toState, refreshKey, reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchLogs() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/modules/audit-logs", {
        params: {
          query: {
            Cursor: pagination.cursor,
            Limit: pagination.limit,
            ClientId: clientId,
            ModuleKey: moduleKey,
            ToState: toState,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load module audit logs.");
      } else {
        setLogs(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchLogs();
    return () => {
      cancelled = true;
    };
  }, [clientId, moduleKey, toState, refreshKey, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Module</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  No module changes.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDateTime(log.createdAt)}
                  </TableCell>
                  <TableCell>
                    <code className="text-xs">{log.moduleKey}</code>
                  </TableCell>
                  <TableCell>
                    <GrantTransition from={log.fromState} to={log.toState} />
                  </TableCell>
                  <TableCell>
                    <IdLink to={`/clients/${log.clientId}`} id={log.clientId} />
                  </TableCell>
                  <TableCell>
                    <ActorBadge actorId={log.actorId} actorType={log.actorType} />
                  </TableCell>
                  <TableCell className="max-w-xs">
                    {log.reason ? (
                      <span className="text-sm">{log.reason}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        limitOptions={[20, 50, 100]}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}

function IdLink({ to, id }: { to: string; id?: string }) {
  if (!id) return <span className="text-muted-foreground">-</span>;
  return (
    <Link to={to}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {id.slice(0, 8)}...
      </Badge>
    </Link>
  );
}

function GrantTransition({
  from,
  to,
}: {
  from: AuditLog["fromState"];
  to: AuditLog["toState"];
}) {
  const granted = to === "Granted";
  return (
    <span className="flex items-center gap-1.5 text-sm">
      <Badge variant={granted ? "default" : "secondary"}>
        {granted ? "Granted" : "Revoked"}
      </Badge>
      <span className="text-muted-foreground text-xs">
        ({from === "Granted" ? "was held" : "not held"})
      </span>
    </span>
  );
}

function ActorBadge({
  actorId,
  actorType,
}: {
  actorId: AuditLog["actorId"];
  actorType: AuditLog["actorType"];
}) {
  if (actorType === "StaffUser" && actorId) {
    return (
      <Link to={`/users/${actorId}`}>
        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
          {actorId.slice(0, 8)}...
        </Badge>
      </Link>
    );
  }
  if (actorType === "AdminApiKey") {
    return <Badge variant="outline">Admin API key</Badge>;
  }
  return <Badge variant="outline">System</Badge>;
}
